'use client';

/**
 * PartnersTable
 *
 * Renders the structured payload returned by `crm_list_partners`.
 * Issue #1432 Phase 2 — Wave 2 P1
 */

import Box from '@mui/material/Box';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { DataTableColumn } from '../../types/dataTable';
import { DataTable } from '../DataTable/DataTable';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import { formatStatusLabel, getStatusColor } from './statusColor';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const PartnerSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  activeDeals: z.number().optional(),
  mtdCommission: z.number().optional(),
});

type PartnerRow = z.infer<typeof PartnerSchema>;

const PartnersDataSchema = z.object({
  partners: z.array(PartnerSchema),
  total: z.number().optional(),
});

const PartnersArraySchema = z.array(PartnerSchema);

function parsePartnersData(data: unknown): PartnerRow[] | null {
  const wrapped = PartnersDataSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.partners;

  const bareArray = PartnersArraySchema.safeParse(data);
  if (bareArray.success) return bareArray.data;

  return null;
}

// ============================================================================
// Component
// ============================================================================

export const PartnersTable = ({ data, onNavigate }: AstorToolRendererProps): ReactElement => {
  const { t, formatCurrency, formatNumber } = useI18n();
  const rows = useMemo(() => parsePartnersData(data), [data]);

  const columns: DataTableColumn<PartnerRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('astorTools.partners.name'),
        accessor: 'name',
      },
      {
        id: 'type',
        header: t('astorTools.partners.type'),
        accessor: 'type',
      },
      {
        id: 'status',
        header: t('astorTools.partners.status'),
        accessor: 'status',
        sortable: true,
        cell: (value) => {
          const status = typeof value === 'string' ? value : '';
          if (!status) return '';
          return (
            <GlassChip
              label={formatStatusLabel(status)}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        id: 'activeDeals',
        header: t('astorTools.partners.activeDeals'),
        accessor: 'activeDeals',
        align: 'right',
        sortable: true,
        cell: (value) => (typeof value === 'number' ? formatNumber(value) : ''),
      },
      {
        id: 'mtdCommission',
        header: t('astorTools.partners.mtdCommission'),
        accessor: 'mtdCommission',
        align: 'right',
        sortable: true,
        cell: (value) => (typeof value === 'number' ? formatCurrency(value) : ''),
      },
    ],
    [t, formatCurrency, formatNumber],
  );

  if (rows === null) {
    return (
      <RendererEmptyState
        title={t('astorTools.error.parseFailed')}
        description={t('astorTools.error.parseFailedDescription')}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <RendererEmptyState
        title={t('astorTools.partners.empty')}
        description={t('astorTools.partners.emptyDescription')}
      />
    );
  }

  return (
    <Box sx={{ my: 1.5 }}>
      <DataTable<PartnerRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        sortable
        paginated
        pagination={{ page: 0, pageSize: 10, pageSizeOptions: [10, 25, 50] }}
        density="compact"
        variant="glass"
        onRowClick={onNavigate ? (row) => onNavigate(`/partners/${row.id}`) : undefined}
      />
    </Box>
  );
};

export default PartnersTable;
